import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Wallet, Lock, AlertTriangle, CheckCircle2, CircleDashed,
  TrendingUp, TrendingDown, ChevronLeft, ChevronRight, Loader2,
  Plus, Trash2, Banknote, Receipt, CalendarCheck, Coins,
  Mail, Download, FileText, Package, BarChart3, Settings2, ShieldAlert,
  ChefHat, Clock, Sparkles, RefreshCw, X,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import api from "../../lib/api";
import useUserPermissions from "../../hooks/useUserPermissions";
import MonthlyLocksCard from "./MonthlyLocksCard";

const fmtEUR = (n) => new Intl.NumberFormat("es-ES", {
  style: "currency", currency: "EUR",
}).format(n || 0);
const fmtEURShort = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}k€`;
  return `${Math.round(v)}€`;
};
const todayIso = () => new Date().toISOString().slice(0, 10);

const MONTH_NAMES_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const METHOD_LABELS = {
  cash: "Efectivo", card: "Tarjeta", transfer: "Transferencia",
  direct_debit: "Domiciliación", other: "Otros",
};
const METHOD_COLORS = {
  cash: "bg-emerald-100 text-emerald-800 border-emerald-200",
  card: "bg-blue-100 text-blue-800 border-blue-200",
  transfer: "bg-indigo-100 text-indigo-800 border-indigo-200",
  direct_debit: "bg-violet-100 text-violet-800 border-violet-200",
  other: "bg-muted text-foreground border-border",
};

export default function ClosingsTab({ restaurantId }) {
  const { can: canDo } = useUserPermissions(restaurantId);
  const canOpenCash = canDo("can_open_cash");
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });
  const [timeline, setTimeline] = useState(null);
  const [loadingTl, setLoadingTl] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [current, setCurrent] = useState(null);
  const [salesPreview, setSalesPreview] = useState(null);
  const [openCashDialog, setOpenCashDialog] = useState(false);
  const [closeCashDialog, setCloseCashDialog] = useState(false);
  const [openForm, setOpenForm] = useState({ session_date: todayIso(), opening_cash: "", cash_register_id: "", shift_id: "" });
  const [closeForm, setCloseForm] = useState({ counted_cash: "", tips: "", payouts: [], closed_notes: "", datafono_total: "", datafono_count: "", datafono_provider: "" });
  const [salesCloseDialog, setSalesCloseDialog] = useState(false);
  const [busy, setBusy] = useState(false);

  const [registers, setRegisters] = useState([]);
  const [shiftsCfg, setShiftsCfg] = useState([]);

  const loadRegistersAndShifts = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([
        api.listCashRegisters(restaurantId).catch(() => ({ cash_registers: [] })),
        api.getShifts(restaurantId).catch(() => ({ shifts: [] })),
      ]);
      setRegisters(r.cash_registers || []);
      setShiftsCfg(s.shifts || []);
    } catch { /* silent */ }
  }, [restaurantId]);

  useEffect(() => { if (restaurantId) loadRegistersAndShifts(); }, [restaurantId, loadRegistersAndShifts]);

  const loadTimeline = useCallback(async () => {
    setLoadingTl(true);
    try {
      const tl = await api.getClosingsTimeline(restaurantId, cursor.year, cursor.month);
      setTimeline(tl);
    } catch (e) {
      toast.error(e.message || "Error al cargar cierres");
    } finally {
      setLoadingTl(false);
    }
  }, [restaurantId, cursor.year, cursor.month]);

  const loadCurrentCash = useCallback(async (day) => {
    try {
      const data = await api.getCurrentCashSession(restaurantId, day);
      setCurrent(data.session);
      setSalesPreview(data.sales_preview);
    } catch {
      setCurrent(null);
    }
  }, [restaurantId]);

  useEffect(() => { loadTimeline(); }, [loadTimeline]);
  useEffect(() => { loadCurrentCash(selectedDay || todayIso()); }, [loadCurrentCash, selectedDay]);

  const goMonth = (delta) => {
    let m = cursor.month + delta;
    let y = cursor.year;
    while (m < 1) { m += 12; y -= 1; }
    while (m > 12) { m -= 12; y += 1; }
    setCursor({ year: y, month: m });
    setSelectedDay(null);
  };

  const daySummary = useMemo(() => {
    if (!timeline || !selectedDay) return null;
    return timeline.days.find(d => d.day === selectedDay) || null;
  }, [timeline, selectedDay]);

  const handleOpenCash = async () => {
    if (!openForm.opening_cash && openForm.opening_cash !== "0") {
      toast.error("Indica el fondo de caja inicial");
      return;
    }
    setBusy(true);
    try {
      await api.openCashSession(restaurantId, {
        session_date: openForm.session_date,
        opening_cash: Number(openForm.opening_cash),
        cash_register_id: openForm.cash_register_id || null,
        shift_id: openForm.shift_id || null,
      });
      toast.success("Caja abierta correctamente");
      setOpenCashDialog(false);
      await loadTimeline();
      await loadCurrentCash(openForm.session_date);
      setSelectedDay(openForm.session_date);
    } catch (e) {
      toast.error(e.message || "No se pudo abrir");
    } finally {
      setBusy(false);
    }
  };

  const handleCloseCash = async () => {
    if (!current || current.status !== "open") return;
    if (closeForm.counted_cash === "") {
      toast.error("Indica el efectivo contado");
      return;
    }
    setBusy(true);
    try {
      const res = await api.closeCashSession(restaurantId, current.session_id, {
        counted_cash: Number(closeForm.counted_cash),
        tips: Number(closeForm.tips || 0),
        payouts: closeForm.payouts.filter(p => p.concept && p.amount).map(p => ({
          concept: p.concept,
          amount: Number(p.amount),
          paid_to: p.paid_to || null,
          create_expense: !!p.create_expense,
          expense_category: p.expense_category || null,
        })),
        closed_notes: closeForm.closed_notes || null,
      });
      const created = Number(res.linked_expense_ids?.length || 0);
      // Also persist the datáfono record if provided — reconciliation in same action
      let datafonoRes = null;
      if (closeForm.datafono_total && Number(closeForm.datafono_total) >= 0) {
        try {
          datafonoRes = await api.upsertDatafonoRecord(restaurantId, {
            session_date: current.session_date,
            cash_register_id: current.cash_register_id || null,
            shift_id: current.shift_id || null,
            datafono_total: Number(closeForm.datafono_total),
            datafono_count: closeForm.datafono_count ? Number(closeForm.datafono_count) : null,
            datafono_provider: (closeForm.datafono_provider || "").trim() || null,
            source: "manual",
          });
        } catch (e) {
          toast.warning(`Arqueo guardado pero datáfono falló: ${e.message || ""}`);
        }
      }
      if (Math.abs(res.discrepancy) < 0.01) {
        toast.success(
          `Caja cuadrada perfectamente (${fmtEUR(res.counted_cash)})` +
          (created ? ` · ${created} gasto${created === 1 ? "" : "s"} contable${created === 1 ? "" : "s"} creado${created === 1 ? "" : "s"}` : "") +
          (res.z_close ? ` · Z-${String(res.z_close.z_number).padStart(3, "0")} emitido` : "")
        );
      } else {
        toast[Math.abs(res.discrepancy) > 10 ? "error" : "warning"](
          `Descuadre de ${fmtEUR(res.discrepancy)} — esperado ${fmtEUR(res.expected_cash)}, contado ${fmtEUR(res.counted_cash)}` +
          (created ? ` · ${created} gasto${created === 1 ? "" : "s"} contable${created === 1 ? "" : "s"} creado${created === 1 ? "" : "s"}` : "") +
          (res.z_close ? ` · Z-${String(res.z_close.z_number).padStart(3, "0")} emitido` : "")
        );
      }
      // Datáfono reconciliation toast
      if (datafonoRes) {
        if (datafonoRes.status === "match") {
          toast.success(`Datáfono conciliado · ${fmtEUR(datafonoRes.record.datafono_total)} = TPV ${fmtEUR(datafonoRes.tpv_card_total)}`);
        } else {
          toast[datafonoRes.status === "mismatch" ? "error" : "warning"](
            `Datáfono vs TPV: ${datafonoRes.delta > 0 ? "+" : ""}${fmtEUR(datafonoRes.delta)} de diferencia`
          );
        }
      }
      setCloseCashDialog(false);
      setCloseForm({ counted_cash: "", tips: "", payouts: [], closed_notes: "", datafono_total: "", datafono_count: "", datafono_provider: "" });
      await loadTimeline();
      await loadCurrentCash(current.session_date);
    } catch (e) {
      toast.error(e.message || "No se pudo cerrar");
    } finally {
      setBusy(false);
    }
  };

  const handleCloseSales = async (day) => {
    setBusy(true);
    try {
      const sessionId = current?.session_id || null;
      await api.closeSalesDay(restaurantId, {
        day,
        linked_cash_session_id: sessionId,
      });
      toast.success(`Día de ventas cerrado (${day})`);
      setSalesCloseDialog(false);
      await loadTimeline();
    } catch (e) {
      toast.error(e.message || "No se pudo cerrar");
    } finally {
      setBusy(false);
    }
  };

  const statusPill = (day) => {
    // Composite status for a day cell
    const states = [];
    if (day.cash_status === "discrepancy") states.push("r");
    else if (day.cash_status === "closed") states.push("g");
    else if (day.cash_status === "open") states.push("y");
    if (day.sales_closed) states.push("g");
    else if (day.sales_total > 0) states.push("y");
    return states;
  };

  return (
    <div className="space-y-5" data-testid="closings-tab">
      {/* ───── Header with today's quick actions ───── */}
      <div className="grid lg:grid-cols-3 gap-3">
        <Card className="lg:col-span-2" data-testid="closings-today-card">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Hoy · {todayIso()}</div>
                <h3 className="text-lg font-semibold flex items-center gap-2 mt-0.5">
                  <Wallet className="h-4 w-4 text-violet-600" />
                  Estado de la caja
                </h3>
              </div>
              <div className="flex gap-2 flex-wrap">
                {!current && (
                  <Button
                    size="sm"
                    onClick={() => setOpenCashDialog(true)}
                    disabled={!canOpenCash}
                    title={canOpenCash ? "" : "Pide a tu encargado que active tu permiso de caja"}
                    data-testid="open-cash-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Abrir caja
                  </Button>
                )}
                {current && current.status === "open" && (
                  <Button
                    size="sm"
                    onClick={() => {
                      setCloseForm({ counted_cash: "", tips: "", payouts: [], closed_notes: "" });
                      setCloseCashDialog(true);
                    }}
                    disabled={!canOpenCash}
                    title={canOpenCash ? "" : "Pide a tu encargado que active tu permiso de caja"}
                    data-testid="close-cash-btn"
                  >
                    <Lock className="h-4 w-4 mr-1" /> Arquear y cerrar
                  </Button>
                )}
                <Button
                  size="sm" variant="outline" onClick={() => setSalesCloseDialog(true)}
                  data-testid="close-sales-day-btn"
                >
                  <CalendarCheck className="h-4 w-4 mr-1" /> Cerrar día ventas
                </Button>
                <DailyReportActions
                  restaurantId={restaurantId}
                  day={todayIso()}
                  size="sm"
                  variant="outline"
                />
              </div>
            </div>

            {current ? (
              <div className="mt-3 grid sm:grid-cols-4 gap-2 text-sm">
                <Metric label="Fondo inicial" value={fmtEUR(current.opening_cash)} />
                <Metric
                  label="Ventas efectivo"
                  value={fmtEUR(salesPreview?.cash_total || 0)}
                  hint={`${salesPreview?.transactions || 0} ventas en total`}
                />
                <Metric
                  label="Estado"
                  value={current.status === "open" ? "Abierta" : "Cerrada"}
                  tone={current.status === "open" ? "amber" : "emerald"}
                />
                <Metric
                  label={current.status === "closed" ? "Descuadre" : "Esperado"}
                  value={fmtEUR(
                    current.status === "closed"
                      ? current.discrepancy
                      : (current.opening_cash + (salesPreview?.cash_total || 0))
                  )}
                  tone={
                    current.status === "closed"
                      ? (Math.abs(current.discrepancy || 0) < 0.5 ? "emerald"
                        : Math.abs(current.discrepancy || 0) > 10 ? "rose" : "amber")
                      : "gray"
                  }
                />
              </div>
            ) : (
              <div className="mt-3 text-sm text-muted-foreground">
                No hay caja abierta para hoy. Abre la caja antes de empezar el turno para
                poder arquear correctamente al final del día.
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="closings-sales-today-card">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Ventas hoy</div>
            <div className="text-2xl font-bold mt-0.5">
              {fmtEUR(salesPreview?.grand_total || 0)}
            </div>
            <div className="text-[11px] text-muted-foreground mb-2">
              {salesPreview?.transactions || 0} tickets · Ticket medio {fmtEUR(salesPreview?.avg_ticket || 0)}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(salesPreview?.by_method || {}).map(([m, v]) => (
                <span
                  key={m}
                  className={`text-[10px] px-1.5 py-0.5 rounded border ${METHOD_COLORS[m] || METHOD_COLORS.other}`}
                >
                  {METHOD_LABELS[m] || m}: {fmtEURShort(v.total)}
                </span>
              ))}
              {(!salesPreview?.by_method || Object.keys(salesPreview.by_method).length === 0) && (
                <span className="text-[11px] text-muted-foreground">Sin ventas</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ───── Month timeline ───── */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => goMonth(-1)} data-testid="tl-prev">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="font-semibold min-w-[150px] text-center" data-testid="tl-month-label">
                {MONTH_NAMES_ES[cursor.month - 1]} {cursor.year}
              </div>
              <Button variant="outline" size="sm" onClick={() => goMonth(1)} data-testid="tl-next">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
              <Legend dot="bg-emerald-500" label="Cuadrada" />
              <Legend dot="bg-amber-500" label="Abierta / pendiente" />
              <Legend dot="bg-rose-500" label="Descuadre" />
              <Legend dot="bg-gray-300" label="Sin actividad" />
            </div>
          </div>

          {loadingTl ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="closings-timeline">
                <thead>
                  <tr className="text-[11px] uppercase text-muted-foreground border-b">
                    <th className="text-left py-2 pl-2">Día</th>
                    <th className="text-right px-2">Ventas</th>
                    <th className="text-center px-2">Caja</th>
                    <th className="text-center px-2">Ventas cerradas</th>
                    <th className="text-center px-2">Inventario</th>
                    <th className="text-center px-2">Producción</th>
                    <th className="text-right pr-2">Descuadre</th>
                  </tr>
                </thead>
                <tbody>
                  {timeline?.days?.map(d => {
                    const hasAny = d.sales_total > 0 || d.cash_status !== "none" || d.sales_closed;
                    const isSelected = d.day === selectedDay;
                    return (
                      <tr
                        key={d.day}
                        onClick={() => setSelectedDay(isSelected ? null : d.day)}
                        data-testid={`tl-day-${d.day}`}
                        className={`border-b border-border transition cursor-pointer ${
                          isSelected ? "bg-violet-50" :
                          d.is_future ? "opacity-40" :
                          hasAny ? "hover:bg-gray-50" : "opacity-60"
                        }`}
                      >
                        <td className="py-1.5 pl-2 font-medium">
                          {d.day.slice(8, 10)}
                          <span className="text-muted-foreground text-[11px] ml-1">
                            {new Date(d.day).toLocaleDateString("es-ES", { weekday: "short" })}
                          </span>
                        </td>
                        <td className="text-right px-2 tabular-nums">
                          {d.sales_total > 0 ? fmtEURShort(d.sales_total) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="text-center px-2">
                          <StatusDot status={d.cash_status} />
                        </td>
                        <td className="text-center px-2">
                          {d.sales_closed
                            ? <CheckCircle2 className="h-4 w-4 text-emerald-500 inline" />
                            : (d.sales_total > 0 && !d.is_future)
                              ? <CircleDashed className="h-4 w-4 text-amber-400 inline" />
                              : <span className="text-gray-300">—</span>
                          }
                        </td>
                        <td className="text-center px-2">
                          {d.inventory_status === "closed" ? (
                            <span
                              className="inline-flex items-center gap-0.5 text-[11px] text-emerald-700 font-medium"
                              title={`Inventario cerrado · ${fmtEURShort(d.inventory_value || 0)}`}
                              data-testid={`tl-inventory-closed-${d.day}`}
                            >
                              <CheckCircle2 className="h-3 w-3" />
                              {fmtEURShort(d.inventory_value || 0)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-[11px]" title="Sin cierre de inventario">·</span>
                          )}
                        </td>
                        <td className="text-center px-2">
                          {d.production_status === "closed" ? (
                            <span
                              className="inline-flex items-center gap-0.5 text-[11px] text-orange-700 font-medium"
                              title={`${d.production_items_count} preparaciones · ${d.production_waste_count} mermas · ${fmtEURShort(d.production_waste_value || 0)}`}
                              data-testid={`tl-production-closed-${d.day}`}
                            >
                              <ChefHat className="h-3 w-3" />
                              {d.production_items_count}
                              {(d.production_waste_count || 0) > 0 && (
                                <span className="ml-0.5 text-rose-700">
                                  · ⚠{d.production_waste_count}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-[11px]" title="Sin cierre de producción">·</span>
                          )}
                        </td>
                        <td className="text-right pr-2 tabular-nums">
                          {d.cash_discrepancy !== null && d.cash_discrepancy !== undefined ? (
                            <span className={`inline-flex items-center gap-0.5 font-medium ${
                              Math.abs(d.cash_discrepancy) < 0.5 ? "text-emerald-700"
                              : d.cash_discrepancy > 0 ? "text-amber-700" : "text-rose-700"
                            }`}>
                              {d.cash_discrepancy > 0 ? <TrendingUp className="h-3 w-3" /> : d.cash_discrepancy < 0 ? <TrendingDown className="h-3 w-3" /> : null}
                              {d.cash_discrepancy > 0 ? "+" : ""}{d.cash_discrepancy.toFixed(2)}€
                            </span>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Month-level summary */}
          {timeline && (
            <div className="mt-3 text-[11px] text-muted-foreground flex items-center gap-3 flex-wrap">
              <span>
                Mes de gastos:
                {timeline.expenses_month_closed
                  ? <span className="ml-1 inline-flex items-center gap-1 text-emerald-700 font-medium">
                      <Lock className="h-3 w-3" /> Cerrado y enviado al contable
                    </span>
                  : <span className="ml-1 text-amber-700 font-medium">Aún sin enviar</span>}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ───── Day detail side panel ───── */}
      {daySummary && (
        <DayDetail
          day={daySummary}
          restaurantId={restaurantId}
          onClose={() => setSelectedDay(null)}
          onRefresh={() => { loadTimeline(); loadCurrentCash(daySummary.day); }}
        />
      )}

      {/* ───── Monthly pre-closing checklist ───── */}
      <MonthlyPreCloseChecklist
        restaurantId={restaurantId}
        year={cursor.year}
        month={cursor.month}
      />

      {/* ───── Historical inventory snapshots ───── */}
      <InventoryClosingsHistory restaurantId={restaurantId} />

      {/* ───── AI learnings from drop-alert feedback ───── */}
      <AiLearningsCard restaurantId={restaurantId} />

      {/* ───── Open cash dialog ───── */}
      <Dialog open={openCashDialog} onOpenChange={setOpenCashDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-4 w-4" /> Abrir caja
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Fecha</Label>
              <Input
                type="date" value={openForm.session_date}
                onChange={(e) => setOpenForm(f => ({ ...f, session_date: e.target.value }))}
              />
            </div>
            {registers.length > 1 && (
              <div className="space-y-1.5">
                <Label>Caja</Label>
                <select
                  className="w-full h-10 rounded border px-2 text-sm bg-card"
                  value={openForm.cash_register_id}
                  onChange={(e) => setOpenForm(f => ({ ...f, cash_register_id: e.target.value }))}
                  data-testid="open-cash-register-select"
                >
                  <option value="">— Auto (primera caja activa) —</option>
                  {registers.filter(r => r.active).map(r => (
                    <option key={r.register_id} value={r.register_id}>
                      {r.name}{r.zone ? ` · ${r.zone}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {shiftsCfg.length > 1 && (
              <div className="space-y-1.5">
                <Label>Turno</Label>
                <select
                  className="w-full h-10 rounded border px-2 text-sm bg-card"
                  value={openForm.shift_id}
                  onChange={(e) => setOpenForm(f => ({ ...f, shift_id: e.target.value }))}
                  data-testid="open-cash-shift-select"
                >
                  <option value="">— Auto por hora actual —</option>
                  {shiftsCfg.map(s => (
                    <option key={s.shift_id} value={s.shift_id}>
                      {s.name} ({String(s.start_hour).padStart(2, "0")}:00 → {String(s.end_hour).padStart(2, "0")}:00)
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Fondo de caja inicial (€)</Label>
              <Input
                type="number" min={0} step="0.01" value={openForm.opening_cash}
                onChange={(e) => setOpenForm(f => ({ ...f, opening_cash: e.target.value }))}
                placeholder="Ej. 150"
                data-testid="open-cash-amount"
              />
              <p className="text-[11px] text-muted-foreground">
                Efectivo con el que arrancas el turno (cambios, billetes pequeños).
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCashDialog(false)}>Cancelar</Button>
            <Button onClick={handleOpenCash} disabled={busy} data-testid="confirm-open-cash">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Abrir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ───── Close cash dialog ───── */}
      <Dialog open={closeCashDialog} onOpenChange={setCloseCashDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-4 w-4" /> Arqueo y cierre de caja
            </DialogTitle>
          </DialogHeader>
          {current && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-3 gap-2">
                <Metric label="Fondo inicial" value={fmtEUR(current.opening_cash)} />
                <Metric
                  label="Ventas efectivo"
                  value={fmtEUR(salesPreview?.cash_total || 0)}
                />
                <Metric
                  label="Esperado (bruto)"
                  value={fmtEUR((current.opening_cash || 0) + (salesPreview?.cash_total || 0))}
                  tone="violet"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Efectivo contado (€)</Label>
                  <Input
                    type="number" min={0} step="0.01" value={closeForm.counted_cash}
                    onChange={(e) => setCloseForm(f => ({ ...f, counted_cash: e.target.value }))}
                    data-testid="close-cash-counted"
                    autoFocus
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Propinas en efectivo (€)</Label>
                  <Input
                    type="number" min={0} step="0.01" value={closeForm.tips}
                    onChange={(e) => setCloseForm(f => ({ ...f, tips: e.target.value }))}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Retiros y pagos de caja</Label>
                  <button
                    type="button"
                    onClick={() => setCloseForm(f => ({
                      ...f, payouts: [...f.payouts, { concept: "", amount: "", paid_to: "", create_expense: false, expense_category: "" }],
                    }))}
                    className="text-[11px] text-violet-700 hover:text-violet-900 underline"
                    data-testid="add-payout"
                  >
                    + añadir retiro
                  </button>
                </div>
                {closeForm.payouts.length === 0 && (
                  <p className="text-[11px] text-muted-foreground">
                    Sangrías al banco, pagos a proveedores desde caja, vales a empleados…
                  </p>
                )}
                {closeForm.payouts.map((p, idx) => (
                  <div key={idx} className="rounded-md border p-2 bg-muted/50/60 space-y-2" data-testid={`payout-row-${idx}`}>
                    <div className="grid grid-cols-[2fr_1fr_1fr_auto] gap-2 items-center">
                      <Input
                        placeholder="Concepto (ej. Proveedor Makro)"
                        value={p.concept}
                        onChange={(e) => setCloseForm(f => {
                          const np = [...f.payouts]; np[idx] = { ...np[idx], concept: e.target.value };
                          return { ...f, payouts: np };
                        })}
                      />
                      <Input
                        placeholder="A quién"
                        value={p.paid_to}
                        onChange={(e) => setCloseForm(f => {
                          const np = [...f.payouts]; np[idx] = { ...np[idx], paid_to: e.target.value };
                          return { ...f, payouts: np };
                        })}
                      />
                      <Input
                        type="number" min={0} step="0.01" placeholder="€"
                        value={p.amount}
                        onChange={(e) => setCloseForm(f => {
                          const np = [...f.payouts]; np[idx] = { ...np[idx], amount: e.target.value };
                          return { ...f, payouts: np };
                        })}
                      />
                      <button
                        type="button"
                        onClick={() => setCloseForm(f => ({
                          ...f, payouts: f.payouts.filter((_, i) => i !== idx),
                        }))}
                        className="text-muted-foreground hover:text-rose-700 p-1"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <label className="flex items-center gap-1.5 text-[11px] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!p.create_expense}
                          onChange={(e) => setCloseForm(f => {
                            const np = [...f.payouts]; np[idx] = { ...np[idx], create_expense: e.target.checked };
                            return { ...f, payouts: np };
                          })}
                          className="h-3.5 w-3.5 rounded border-border text-violet-600 focus:ring-violet-500"
                          data-testid={`payout-create-expense-${idx}`}
                        />
                        <Receipt className="h-3 w-3 text-violet-600" />
                        <span className="font-medium text-foreground">
                          Crear gasto contable automáticamente
                        </span>
                      </label>
                      {p.create_expense && (
                        <select
                          value={p.expense_category}
                          onChange={(e) => setCloseForm(f => {
                            const np = [...f.payouts]; np[idx] = { ...np[idx], expense_category: e.target.value };
                            return { ...f, payouts: np };
                          })}
                          className="text-[11px] px-1.5 py-0.5 rounded border border-border bg-card focus:border-violet-400 focus:ring-violet-200"
                          data-testid={`payout-category-${idx}`}
                        >
                          <option value="">Auto (según concepto)</option>
                          <option value="purchases">Compras a proveedores</option>
                          <option value="utilities">Suministros</option>
                          <option value="telecommunications">Telecomunicaciones</option>
                          <option value="rent">Alquiler</option>
                          <option value="payroll">Nóminas</option>
                          <option value="taxes">Impuestos</option>
                          <option value="marketing">Marketing</option>
                          <option value="maintenance">Mantenimiento</option>
                          <option value="cleaning">Limpieza</option>
                          <option value="software">Software</option>
                          <option value="delivery_apps">Apps delivery</option>
                          <option value="bank_fees">Comisiones bancarias</option>
                          <option value="professional">Servicios profesionales</option>
                          <option value="insurance">Seguros</option>
                          <option value="other">Otros</option>
                        </select>
                      )}
                      {p.create_expense && (
                        <span className="text-[10px] text-violet-700/80">
                          Se creará en Gastos con método=efectivo.
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-1.5">
                <Label>Notas (opcional)</Label>
                <Input
                  value={closeForm.closed_notes}
                  onChange={(e) => setCloseForm(f => ({ ...f, closed_notes: e.target.value }))}
                  placeholder="Cualquier detalle del cierre…"
                />
              </div>

              {/* ── Datáfono reconciliation (Feature #124/125) ── */}
              <div className="rounded-lg border border-sky-200 bg-sky-50/50 p-3 space-y-2" data-testid="close-cash-datafono-section">
                <div className="flex items-center gap-2 text-[11px] text-sky-900">
                  <span className="font-semibold">Reconciliación con datáfono</span>
                  <span className="text-sky-600">(opcional)</span>
                  <label className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded bg-sky-600 text-white text-[10px] cursor-pointer hover:bg-sky-700 transition" data-testid="datafono-ocr-trigger">
                    <Sparkles className="h-2.5 w-2.5" />
                    <span>Escanear ticket</span>
                    <input
                      type="file" accept="image/*" className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = async (ev) => {
                          const b64 = String(ev.target?.result || "").split(",")[1];
                          if (!b64) { toast.error("No se pudo leer la imagen"); return; }
                          toast.loading("Leyendo ticket datáfono…", { id: "ocr-dataf" });
                          try {
                            const ocr = await api.ocrDatafonoTicket(restaurantId, {
                              imageB64: b64, mimeType: file.type || "image/jpeg",
                              sessionDate: current?.session_date,
                            });
                            const ex = ocr.extracted || {};
                            setCloseForm(f => ({
                              ...f,
                              datafono_total: ex.total != null ? String(ex.total) : f.datafono_total,
                              datafono_count: ex.operations_count != null ? String(ex.operations_count) : f.datafono_count,
                              datafono_provider: ex.provider || f.datafono_provider,
                            }));
                            toast.success(
                              `Ticket leído · ${ex.provider || "?"} · ${ex.total != null ? ex.total + "€" : "sin total"} · confianza ${(ocr.confidence * 100).toFixed(0)}%`,
                              { id: "ocr-dataf" }
                            );
                          } catch (err) {
                            toast.error(err?.message || "Error al leer ticket", { id: "ocr-dataf" });
                          }
                          e.target.value = "";
                        };
                        reader.readAsDataURL(file);
                      }}
                    />
                  </label>
                </div>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-5">
                    <Label className="text-[10px]">Total datáfono (€)</Label>
                    <Input
                      type="number" min={0} step="0.01"
                      value={closeForm.datafono_total}
                      onChange={(e) => setCloseForm(f => ({ ...f, datafono_total: e.target.value }))}
                      placeholder="Ej. 842.50"
                      className="h-8 text-xs"
                      data-testid="close-cash-datafono-total"
                    />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[10px]">Operaciones</Label>
                    <Input
                      type="number" min={0}
                      value={closeForm.datafono_count}
                      onChange={(e) => setCloseForm(f => ({ ...f, datafono_count: e.target.value }))}
                      placeholder="15"
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="col-span-4">
                    <Label className="text-[10px]">Proveedor</Label>
                    <Input
                      value={closeForm.datafono_provider}
                      onChange={(e) => setCloseForm(f => ({ ...f, datafono_provider: e.target.value }))}
                      placeholder="Redsys"
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-sky-700">
                  Pega el total del corte del datáfono o sube una foto para autocompletar con IA. Detectaremos tickets perdidos comparando con el TPV.
                </p>
              </div>

              {closeForm.counted_cash !== "" && (
                <DiscrepancyPreview
                  opening={current.opening_cash}
                  cash={salesPreview?.cash_total || 0}
                  tips={Number(closeForm.tips || 0)}
                  payouts={closeForm.payouts.reduce((s, p) => s + Number(p.amount || 0), 0)}
                  counted={Number(closeForm.counted_cash)}
                />
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseCashDialog(false)}>Cancelar</Button>
            <Button onClick={handleCloseCash} disabled={busy} data-testid="confirm-close-cash">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="h-4 w-4 mr-1" /> Confirmar cierre</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ───── Close sales-day dialog ───── */}
      <Dialog open={salesCloseDialog} onOpenChange={setSalesCloseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarCheck className="h-4 w-4" /> Cerrar día de ventas
            </DialogTitle>
          </DialogHeader>
          <SalesDayCloseBody
            restaurantId={restaurantId}
            day={selectedDay || todayIso()}
            onClose={handleCloseSales}
            busy={busy}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSalesCloseDialog(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Monthly fiscal locks card ── */}
      <MonthlyLocksCard restaurantId={restaurantId} />
    </div>
  );
}

function Metric({ label, value, hint, tone = "gray" }) {
  const colors = {
    gray: "bg-muted/50 text-foreground",
    emerald: "bg-emerald-50 text-emerald-800",
    amber: "bg-amber-50 text-amber-800",
    rose: "bg-rose-50 text-rose-800",
    violet: "bg-violet-50 text-violet-800",
  };
  return (
    <div className={`rounded-md px-3 py-2 border ${colors[tone] || colors.gray}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-base font-bold tabular-nums">{value}</div>
      {hint && <div className="text-[10px] opacity-60 mt-0.5">{hint}</div>}
    </div>
  );
}

function Legend({ dot, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`h-2 w-2 rounded-full ${dot}`} /> {label}
    </span>
  );
}

function StatusDot({ status }) {
  const map = {
    closed: { cls: "bg-emerald-500", title: "Cuadrada" },
    open: { cls: "bg-amber-500", title: "Abierta" },
    discrepancy: { cls: "bg-rose-500", title: "Descuadre" },
    none: { cls: "bg-accent", title: "Sin actividad" },
  };
  const m = map[status] || map.none;
  return (
    <span className="inline-flex items-center justify-center">
      <span className={`h-2.5 w-2.5 rounded-full ${m.cls}`} title={m.title} />
    </span>
  );
}

function DiscrepancyPreview({ opening, cash, tips, payouts, counted }) {
  const expected = (opening || 0) + (cash || 0) + (tips || 0) - (payouts || 0);
  const diff = counted - expected;
  const tone = Math.abs(diff) < 0.5 ? "emerald" : Math.abs(diff) > 10 ? "rose" : "amber";
  const tones = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
  };
  return (
    <div className={`rounded-md border p-3 text-sm ${tones[tone]}`} data-testid="cash-discrepancy-preview">
      <div className="flex items-center gap-2 font-medium mb-1">
        {tone === "emerald" ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
        {tone === "emerald"
          ? "La caja cuadra perfectamente"
          : `Descuadre estimado: ${diff > 0 ? "sobran" : "faltan"} ${fmtEUR(Math.abs(diff))}`}
      </div>
      <div className="text-[11px] tabular-nums space-y-0.5 opacity-90">
        <div>Fondo inicial: {fmtEUR(opening)} + Ventas efectivo: {fmtEUR(cash)} + Propinas: {fmtEUR(tips)} − Retiros: {fmtEUR(payouts)}</div>
        <div>= Esperado: <strong>{fmtEUR(expected)}</strong> · Contado: <strong>{fmtEUR(counted)}</strong></div>
      </div>
    </div>
  );
}

function SalesDayCloseBody({ restaurantId, day, onClose, busy }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const p = await api.previewSalesDay(restaurantId, day);
        setPreview(p);
      } catch (e) {
        toast.error(e.message);
      } finally { setLoading(false); }
    })();
  }, [restaurantId, day]);
  if (loading) return <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!preview) return null;
  const t = preview.totals;
  return (
    <div className="space-y-3">
      <div className="text-sm">
        Día <strong>{day}</strong> — {t.transactions} tickets · {fmtEUR(t.grand_total)}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(t.by_method).map(([m, v]) => (
          <span key={m} className={`text-xs px-2 py-1 rounded border ${METHOD_COLORS[m] || METHOD_COLORS.other}`}>
            {METHOD_LABELS[m] || m}: {fmtEUR(v.total)} ({v.count})
          </span>
        ))}
      </div>
      {preview.closed ? (
        <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" />
          Este día ya fue cerrado el {preview.closed.closed_at?.slice(0, 10)}.
          Si lo cierras de nuevo se guardará un nuevo snapshot con los totales actuales.
        </div>
      ) : (
        <div className="p-3 rounded-md bg-violet-50 border border-violet-200 text-violet-800 text-sm">
          Al cerrar el día se bloquean las ventas con <code>is_locked=true</code>.
          Futuras correcciones quedarán visibles como ajustes posteriores al cierre.
        </div>
      )}
      <Button onClick={() => onClose(day)} disabled={busy} className="w-full" data-testid="confirm-close-sales-day">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="h-4 w-4 mr-1" /> {preview.closed ? "Recerrar día" : "Cerrar día de ventas"}</>}
      </Button>
    </div>
  );
}

function DayDetail({ day, restaurantId, onClose, onRefresh }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await api.getCurrentCashSession(restaurantId, day.day);
        setSession(data);
      } finally { setLoading(false); }
    })();
  }, [restaurantId, day.day]);

  return (
    <Card className="border-violet-200" data-testid="day-detail-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2">
            <Receipt className="h-4 w-4 text-violet-600" />
            Detalle del {new Date(day.day).toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </h3>
          <div className="flex items-center gap-2">
            <DailyReportActions restaurantId={restaurantId} day={day.day} size="sm" variant="outline" />
            <button className="text-xs text-muted-foreground hover:text-gray-900" onClick={onClose} data-testid="day-detail-close">
              Cerrar ✕
            </button>
          </div>
        </div>
        {loading ? (
          <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-sm mb-2 flex items-center gap-1.5">
                <Coins className="h-3.5 w-3.5" /> Ventas del día
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <Metric label="Total" value={fmtEUR(session?.sales_preview?.grand_total || 0)} />
                <Metric label="Tickets" value={session?.sales_preview?.transactions || 0} />
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {Object.entries(session?.sales_preview?.by_method || {}).map(([m, v]) => (
                  <span key={m} className={`text-[10px] px-1.5 py-0.5 rounded border ${METHOD_COLORS[m] || METHOD_COLORS.other}`}>
                    {METHOD_LABELS[m] || m}: {fmtEUR(v.total)}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-medium text-sm mb-2 flex items-center gap-1.5">
                <Banknote className="h-3.5 w-3.5" /> Caja
              </h4>
              {session?.session ? (
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <Metric label="Fondo" value={fmtEUR(session.session.opening_cash)} />
                  {session.session.status === "closed" ? (
                    <>
                      <Metric label="Contado" value={fmtEUR(session.session.counted_cash)} />
                      <Metric label="Esperado" value={fmtEUR(session.session.expected_cash)} />
                      <Metric
                        label="Descuadre"
                        value={fmtEUR(session.session.discrepancy)}
                        tone={Math.abs(session.session.discrepancy || 0) < 0.5 ? "emerald"
                              : Math.abs(session.session.discrepancy || 0) > 10 ? "rose" : "amber"}
                      />
                      {Number(session.session.tips || 0) > 0 && (
                        <Metric label="Propinas" value={fmtEUR(session.session.tips)} />
                      )}
                      {Number(session.session.payouts_total || 0) > 0 && (
                        <Metric label="Retiros" value={fmtEUR(session.session.payouts_total)} />
                      )}
                    </>
                  ) : (
                    <Metric label="Estado" value="Abierta" tone="amber" />
                  )}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">Sin caja registrada este día.</div>
              )}
              {day.sales_closed && (
                <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-emerald-700 font-medium">
                  <Lock className="h-3 w-3" /> Día de ventas cerrado y bloqueado
                </div>
              )}
            </div>
          </div>
        )}
        {/* Inventory closing section (P1) */}
        <InventoryClosingSection
          restaurantId={restaurantId}
          day={day.day}
          inventoryStatus={day.inventory_status}
          inventorySnapshotId={day.inventory_snapshot_id}
          inventoryValue={day.inventory_value}
          inventoryDeltaValue={day.inventory_delta_value}
          onRefresh={onRefresh}
        />
        {/* Production closing section (P1) */}
        <ProductionClosingSection
          restaurantId={restaurantId}
          day={day.day}
          productionStatus={day.production_status}
          productionLogId={day.production_log_id}
          productionItemsCount={day.production_items_count}
          productionWasteCount={day.production_waste_count}
          productionWasteValue={day.production_waste_value}
          onRefresh={onRefresh}
        />
      </CardContent>
    </Card>
  );
}


// ── Daily report actions: download PDF + email to accountant ─────────────
function DailyReportActions({ restaurantId, day, size = "sm", variant = "outline" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [defaultEmail, setDefaultEmail] = useState("");
  const [ccText, setCcText] = useState("");   // comma-separated UI text
  const [defaultCc, setDefaultCc] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const resp = await api.get(`/restaurants/${restaurantId}/finance/accountant-email`).catch(() => null);
        const saved = resp?.accountant_email || "";
        const savedCc = resp?.accountant_cc || [];
        setDefaultEmail(saved);
        setEmailTo(saved);
        setDefaultCc(savedCc);
        setCcText(savedCc.join(", "));
      } catch {
        /* ignore */
      }
    })();
  }, [open, restaurantId]);

  const parseCcList = (text) => (text || "")
    .split(",").map(s => s.trim()).filter(Boolean);

  const handleDownload = useCallback(async () => {
    setBusy(true);
    try {
      await api.downloadDailyReportPdf(restaurantId, day);
      toast.success("PDF del cierre descargado");
    } catch (e) {
      toast.error(e?.message || "No se pudo descargar el PDF");
    } finally {
      setBusy(false);
    }
  }, [restaurantId, day]);

  const loadPreview = useCallback(async () => {
    const target = (emailTo || defaultEmail || "").trim();
    const cc = parseCcList(ccText);
    setPreviewLoading(true);
    try {
      const res = await api.previewDailyReportEmail(restaurantId, { day, emailTo: target, cc });
      setPreview(res);
    } catch (e) {
      toast.error(e?.message || "Error generando la vista previa");
    } finally {
      setPreviewLoading(false);
    }
  }, [restaurantId, day, emailTo, defaultEmail, ccText]);

  const handleSend = useCallback(async () => {
    const target = (emailTo || defaultEmail || "").trim();
    if (!target) {
      toast.error("Configura un email o escribe uno antes de enviar");
      return;
    }
    const cc = parseCcList(ccText);
    setBusy(true);
    try {
      const resp = await api.sendDailyReportToAccountant(restaurantId, { day, emailTo: target, cc });
      toast.success(
        cc.length
          ? `Cierre enviado a ${resp.sent_to} + CC (${cc.length})`
          : `Cierre del ${day} enviado a ${resp.sent_to}`,
      );
      setOpen(false);
      setPreview(null);
    } catch (e) {
      toast.error(e?.message || "Error enviando el cierre");
    } finally {
      setBusy(false);
    }
  }, [restaurantId, day, emailTo, defaultEmail, ccText]);

  return (
    <>
      <div className="inline-flex gap-1.5">
        <Button
          size={size}
          variant={variant}
          onClick={handleDownload}
          disabled={busy}
          data-testid={`download-daily-report-${day}`}
          title="Descargar PDF del cierre diario"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
          PDF
        </Button>
        <Button
          size={size}
          variant={variant}
          onClick={() => { setOpen(true); setPreview(null); }}
          disabled={busy}
          data-testid={`send-daily-report-${day}`}
          title="Enviar resumen del cierre diario al contable"
        >
          <Mail className="h-4 w-4 mr-1" />
          Enviar al contable
        </Button>
      </div>
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="daily-report-send-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-violet-600" />
              Enviar cierre diario al contable
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-sm text-muted-foreground">
              Se generará un PDF con el arqueo de caja, ventas por método de pago y
              gastos pagados en efectivo del día <b>{day}</b>.
            </p>
            <div>
              <Label htmlFor="daily-report-email">Destinatario</Label>
              <Input
                id="daily-report-email"
                type="email"
                value={emailTo}
                onChange={(e) => { setEmailTo(e.target.value); setPreview(null); }}
                placeholder="contable@gestoria.com"
                data-testid="daily-report-email-input"
              />
              {defaultEmail && defaultEmail !== emailTo && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  Guardado: {defaultEmail}.{" "}
                  <button className="underline" onClick={() => { setEmailTo(defaultEmail); setPreview(null); }}>
                    Usar guardado
                  </button>
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="daily-report-cc">CC (opcional)</Label>
              <Input
                id="daily-report-cc"
                type="text"
                value={ccText}
                onChange={(e) => { setCcText(e.target.value); setPreview(null); }}
                placeholder="duen1@tu-restaurante.com, auditor@gestoria.com"
                data-testid="daily-report-cc-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Separa múltiples emails con coma. Máx 5.{" "}
                {defaultCc.length > 0 && ccText !== defaultCc.join(", ") && (
                  <button className="underline" onClick={() => { setCcText(defaultCc.join(", ")); setPreview(null); }}>
                    Usar guardados ({defaultCc.length})
                  </button>
                )}
              </p>
            </div>
            <EmailPreviewBlock
              preview={preview}
              loading={previewLoading}
              onLoad={loadPreview}
              testidPrefix="daily-report"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button onClick={handleSend} disabled={busy || !emailTo.trim()} data-testid="confirm-send-daily-report">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Mail className="h-4 w-4 mr-1" />}
              Enviar ahora
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ── Reusable email preview block (subject + sanitised HTML + attachment) ──
function EmailPreviewBlock({ preview, loading, onLoad, testidPrefix = "email" }) {
  return (
    <div className="space-y-2 pt-2 border-t" data-testid={`${testidPrefix}-preview-block`}>
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium">Vista previa del email</Label>
        <Button
          size="sm" variant="outline" type="button"
          onClick={onLoad}
          disabled={loading}
          data-testid={`${testidPrefix}-preview-load`}
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <FileText className="h-3.5 w-3.5 mr-1" />}
          {preview ? "Actualizar vista previa" : "Generar vista previa"}
        </Button>
      </div>
      {preview ? (
        <div className="rounded-lg border bg-muted/50 p-0 overflow-hidden" data-testid={`${testidPrefix}-preview-content`}>
          <div className="bg-card px-3 py-2 border-b text-[12px] space-y-0.5">
            <div><span className="text-muted-foreground">Para:</span> <b>{preview.to_email}</b></div>
            {(preview.cc || []).length > 0 && (
              <div><span className="text-muted-foreground">CC:</span> <b>{(preview.cc || []).join(", ")}</b></div>
            )}
            <div><span className="text-muted-foreground">Asunto:</span> <b>{preview.subject}</b></div>
            <div className="text-[11px] text-muted-foreground">
              Adjunto: {preview.filename} ({Math.round((preview.attachment_size_bytes || 0) / 1024)} KB)
            </div>
          </div>
          <div
            className="bg-card p-3 max-h-64 overflow-auto text-sm"
            data-testid={`${testidPrefix}-preview-html`}
            // Sanitised by backend (server-generated, no user input)
            dangerouslySetInnerHTML={{ __html: preview.html }}
          />
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground italic">
          Haz clic en "Generar vista previa" para ver cómo quedará el email antes de enviarlo.
        </p>
      )}
    </div>
  );
}



// ── Inventory closing section inside DayDetail ─────────────────────────
function InventoryClosingSection({
  restaurantId, day, inventoryStatus, inventorySnapshotId,
  inventoryValue, inventoryDeltaValue, onRefresh,
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const hasSnapshot = inventoryStatus === "closed" && inventorySnapshotId;

  const loadDetail = useCallback(async () => {
    if (!hasSnapshot) return;
    setLoading(true);
    try {
      const d = await api.getInventorySnapshot(restaurantId, inventorySnapshotId);
      setDetail(d);
    } catch (e) {
      toast.error(e?.message || "No se pudo cargar el snapshot");
    } finally {
      setLoading(false);
    }
  }, [restaurantId, inventorySnapshotId, hasSnapshot]);

  useEffect(() => {
    if (expanded && hasSnapshot && !detail) loadDetail();
  }, [expanded, hasSnapshot, detail, loadDetail]);

  const handleCreate = useCallback(async () => {
    setBusy(true);
    try {
      const res = await api.createInventorySnapshot(restaurantId, { day, notes });
      toast.success(`Cierre de inventario creado · ${fmtEUR(res.total_value)} en ${res.items_count} productos`);
      setShowDialog(false);
      setNotes("");
      if (onRefresh) onRefresh();
      setExpanded(true);
    } catch (e) {
      toast.error(e?.message || "Error creando el cierre de inventario");
    } finally {
      setBusy(false);
    }
  }, [restaurantId, day, notes, onRefresh]);

  const handleDelete = useCallback(async () => {
    if (!hasSnapshot) return;
    if (!confirm("¿Eliminar este cierre de inventario? Esta acción es reversible (puedes volver a crearlo).")) return;
    setBusy(true);
    try {
      await api.deleteInventorySnapshot(restaurantId, inventorySnapshotId);
      toast.success("Cierre de inventario eliminado");
      setDetail(null);
      setExpanded(false);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e?.message || "No se pudo eliminar");
    } finally {
      setBusy(false);
    }
  }, [restaurantId, inventorySnapshotId, hasSnapshot, onRefresh]);

  return (
    <div className="mt-4 border-t pt-3" data-testid="inventory-closing-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h4 className="font-medium text-sm flex items-center gap-1.5">
          <Package className="h-3.5 w-3.5 text-emerald-700" /> Cierre de inventario
        </h4>
        {hasSnapshot ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-medium" data-testid="inventory-closing-value">
              <CheckCircle2 className="h-3 w-3" />
              Valor: <b>{fmtEUR(inventoryValue || 0)}</b>
              {inventoryDeltaValue !== null && inventoryDeltaValue !== undefined && (
                <span className={
                  inventoryDeltaValue > 0 ? "text-emerald-700" : inventoryDeltaValue < 0 ? "text-rose-700" : "text-muted-foreground"
                }>
                  {" "}({inventoryDeltaValue > 0 ? "+" : ""}{fmtEUR(inventoryDeltaValue)} vs anterior)
                </span>
              )}
            </span>
            <Button
              size="sm" variant="outline"
              onClick={() => setExpanded(v => !v)}
              data-testid="inventory-closing-toggle"
            >
              {expanded ? "Ocultar detalles" : "Ver detalles"}
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={() => setShowDialog(true)}
              disabled={busy}
              data-testid="inventory-closing-rebuild"
              title="Recalcular con el stock actual"
            >
              Recalcular
            </Button>
            <Button
              size="sm" variant="ghost"
              onClick={handleDelete}
              disabled={busy}
              className="text-rose-700 hover:text-rose-700 hover:bg-rose-50"
              data-testid="inventory-closing-delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            onClick={() => setShowDialog(true)}
            data-testid="inventory-closing-create"
          >
            <Package className="h-3.5 w-3.5 mr-1" /> Cerrar inventario
          </Button>
        )}
      </div>

      {hasSnapshot && expanded && (
        <div className="mt-3 rounded-lg border bg-muted/50 p-3 text-sm" data-testid="inventory-closing-detail">
          {loading ? (
            <div className="flex justify-center py-3"><Loader2 className="h-4 w-4 animate-spin" /></div>
          ) : detail ? (
            <>
              <div className="grid sm:grid-cols-4 gap-2">
                <Metric label="Valor total" value={fmtEUR(detail.total_value)} tone="emerald" />
                <Metric label="Productos" value={detail.items_count} />
                <Metric label="Sin coste" value={detail.missing_cost_count} tone={detail.missing_cost_count > 0 ? "amber" : undefined} />
                <Metric label="Bajo mínimo" value={detail.below_min_count} tone={detail.below_min_count > 0 ? "amber" : undefined} />
              </div>
              {Object.keys(detail.by_category || {}).length > 1 && (
                <div className="mt-3">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Por categoría</div>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(detail.by_category || {})
                      .sort((a, b) => (b[1].value || 0) - (a[1].value || 0))
                      .map(([cat, v]) => (
                        <span key={cat} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border bg-card">
                          <span className="font-medium">{cat}</span>
                          <span className="text-muted-foreground">·</span>
                          <span>{v.count}</span>
                          <span className="text-muted-foreground">·</span>
                          <span className="tabular-nums">{fmtEUR(v.value)}</span>
                        </span>
                      ))}
                  </div>
                </div>
              )}
              <div className="mt-3">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Top 10 por valor</div>
                <div className="max-h-64 overflow-auto rounded border bg-card">
                  <table className="w-full text-xs">
                    <thead className="bg-muted text-muted-foreground">
                      <tr>
                        <th className="text-left py-1 px-2">Producto</th>
                        <th className="text-right px-2">Stock</th>
                        <th className="text-right px-2">Coste</th>
                        <th className="text-right px-2 pr-2">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.items || []).slice(0, 10).map(it => (
                        <tr key={it.ingredient_id} className="border-t border-border">
                          <td className="py-1 px-2 truncate max-w-[240px]">{it.name}</td>
                          <td className="text-right px-2 tabular-nums">{it.qty} {it.unit}</td>
                          <td className="text-right px-2 tabular-nums text-muted-foreground">{fmtEUR(it.cost_per_unit)}</td>
                          <td className="text-right px-2 pr-2 tabular-nums font-medium">{fmtEUR(it.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {detail.notes && (
                <p className="mt-2 text-[11px] text-muted-foreground italic">Nota: {detail.notes}</p>
              )}
            </>
          ) : (
            <p className="text-muted-foreground">No se pudo cargar el detalle.</p>
          )}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={(v) => !busy && setShowDialog(v)}>
        <DialogContent data-testid="inventory-closing-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-emerald-700" />
              {hasSnapshot ? "Recalcular cierre de inventario" : "Cerrar inventario del día"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2 text-sm">
            <p className="text-muted-foreground">
              Se guardará un snapshot valorado (cantidad × coste medio) del stock actual
              para el día <b>{day}</b>. Podrás compararlo con días anteriores y ver la evolución del valor del inventario.
            </p>
            {hasSnapshot && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                Ya existe un cierre para este día. Al continuar se sobrescribirá con el stock actual.
              </p>
            )}
            <div>
              <Label htmlFor="inventory-closing-notes">Nota (opcional)</Label>
              <Input
                id="inventory-closing-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="p. ej. Cierre de mes, verificado con conteo físico"
                data-testid="inventory-closing-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={busy} data-testid="inventory-closing-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Package className="h-4 w-4 mr-1" />}
              {hasSnapshot ? "Recalcular y guardar" : "Guardar cierre"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ── Historical inventory closings panel (below the timeline) ──────────
export function InventoryClosingsHistory({ restaurantId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastAlert, setLastAlert] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showRca, setShowRca] = useState(false);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const [data, hist] = await Promise.all([
        api.listInventorySnapshots(restaurantId, { limit: 12 }),
        api.getInventoryDropAlertHistory(restaurantId, 1).catch(() => ({ history: [] })),
      ]);
      setRows(data.snapshots || []);
      setLastAlert((hist.history || [])[0] || null);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (!rows.length) return null;

  const max = Math.max(...rows.map(r => r.total_value || 0), 1);

  return (
    <Card data-testid="inventory-history-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-semibold flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-emerald-700" />
            Evolución del valor de inventario
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] text-muted-foreground">
              Últimos {rows.length} cierres
            </span>
            <Button
              size="sm" variant="outline"
              onClick={() => setShowSettings(true)}
              data-testid="inventory-drop-alert-settings-btn"
            >
              <Settings2 className="h-3.5 w-3.5 mr-1" /> Alertas de merma oculta
            </Button>
          </div>
        </div>
        {lastAlert?.triggered && (
          <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm flex items-start gap-2" data-testid="inventory-drop-last-alert">
            <ShieldAlert className="h-5 w-5 text-rose-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-rose-900">
                Posible merma oculta detectada · {lastAlert.drop_pct}% de caída
              </div>
              <div className="text-[12px] text-rose-800 mt-0.5">
                De <b>{fmtEUR(lastAlert.previous_value)}</b> ({lastAlert.previous_day}) a <b>{fmtEUR(lastAlert.current_value)}</b> ({lastAlert.current_day}).
                Sólo {fmtEUR(lastAlert.purchases_in_window)} en compras en ese periodo →
                <b> {fmtEUR(lastAlert.unexplained_drop)}</b> sin justificar.
              </div>
              <div className="mt-2">
                <Button
                  size="sm" variant="outline"
                  className="border-rose-300 text-rose-700 hover:bg-rose-100"
                  onClick={() => setShowRca(true)}
                  data-testid="inventory-drop-analyze-btn"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-1" /> Analizar causas con IA
                </Button>
              </div>
            </div>
          </div>
        )}
        <div className="space-y-1.5">
          {rows.slice().reverse().map(r => {
            const pct = Math.max(2, Math.round(((r.total_value || 0) / max) * 100));
            const delta = r.delta_value;
            return (
              <div key={r.snapshot_id} className="flex items-center gap-3" data-testid={`inventory-history-row-${r.day}`}>
                <div className="w-24 text-[11px] text-muted-foreground tabular-nums">
                  {r.day}
                </div>
                <div className="flex-1 relative h-6 bg-muted rounded overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-400 to-teal-500 rounded"
                    style={{ width: `${pct}%` }}
                  />
                  <div className="absolute inset-0 flex items-center justify-between px-2 text-[11px] font-medium">
                    <span className="text-white drop-shadow-sm">{fmtEUR(r.total_value || 0)}</span>
                    {delta !== null && delta !== undefined && (
                      <span className={
                        delta > 0 ? "text-emerald-900" : delta < 0 ? "text-rose-900" : "text-foreground"
                      }>
                        {delta > 0 ? "+" : ""}{fmtEUR(delta)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="w-14 text-right text-[11px] text-muted-foreground tabular-nums">
                  {r.items_count} ítems
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
      {showSettings && (
        <InventoryDropAlertSettings
          restaurantId={restaurantId}
          onClose={() => setShowSettings(false)}
          onAfterRun={load}
        />
      )}
      {showRca && (
        <InventoryDropRcaDialog
          restaurantId={restaurantId}
          onClose={() => setShowRca(false)}
        />
      )}
    </Card>
  );
}


// ── Settings modal for the hidden-shrinkage detector ─────────────────
function InventoryDropAlertSettings({ restaurantId, onClose, onAfterRun }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const c = await api.getInventoryDropAlertConfig(restaurantId);
        setCfg(c);
      } catch (e) {
        toast.error(e?.message || "No se pudo cargar la configuración");
      } finally {
        setLoading(false);
      }
    })();
  }, [restaurantId]);

  const persist = useCallback(async (patch) => {
    setSaving(true);
    try {
      const c = await api.updateInventoryDropAlertConfig(restaurantId, patch);
      setCfg(c);
    } catch (e) {
      toast.error(e?.message || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  }, [restaurantId]);

  const runNow = useCallback(async () => {
    setRunning(true);
    try {
      const res = await api.runInventoryDropAlertNow(restaurantId);
      setPreview(res.verdict || null);
      if (!res.verdict) {
        toast.info("No hay suficientes cierres de inventario para evaluar todavía.");
      } else if (res.verdict.triggered) {
        toast.warning(`Alerta disparada: ${res.verdict.drop_pct}% de caída`);
      } else {
        toast.success(`Todo OK: caída actual ${res.verdict.drop_pct}% (por debajo del umbral)`);
      }
      if (onAfterRun) onAfterRun();
    } catch (e) {
      toast.error(e?.message || "Error ejecutando la evaluación");
    } finally {
      setRunning(false);
    }
  }, [restaurantId, onAfterRun]);

  return (
    <Dialog open={true} onOpenChange={(v) => !saving && !running && !v && onClose()}>
      <DialogContent data-testid="inventory-drop-alert-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-rose-700" />
            Alertas de merma oculta en el inventario
          </DialogTitle>
        </DialogHeader>
        {loading || !cfg ? (
          <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="space-y-4 pt-2 text-sm">
            <p className="text-muted-foreground text-[13px]">
              Compara el último cierre de inventario con uno anterior y te avisa si la caída de valor supera el umbral <b>sin justificarse por compras</b> — señal típica de mermas no declaradas o robo silencioso.
            </p>

            <div className="flex items-center justify-between rounded-lg border bg-muted/50 p-3">
              <div>
                <div className="font-medium">Detección automática</div>
                <p className="text-[11px] text-muted-foreground">
                  Se ejecuta cada día a las 08:00 UTC.
                </p>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cfg.enabled}
                  onChange={(e) => persist({ enabled: e.target.checked })}
                  disabled={saving}
                  data-testid="inventory-drop-enabled-switch"
                  className="h-4 w-4 accent-rose-600"
                />
                <span className="text-[12px]">{cfg.enabled ? "Activa" : "Inactiva"}</span>
              </label>
            </div>

            <div>
              <Label className="text-xs font-medium">Umbral mínimo de caída (%)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  min={5} max={50}
                  value={cfg.threshold_pct}
                  onChange={(e) => setCfg({ ...cfg, threshold_pct: parseInt(e.target.value, 10) || 5 })}
                  onBlur={(e) => {
                    const v = Math.max(5, Math.min(50, parseInt(e.target.value, 10) || 15));
                    persist({ threshold_pct: v });
                  }}
                  className="h-8 w-24"
                  disabled={saving}
                  data-testid="inventory-drop-threshold-input"
                />
                <span className="text-[11px] text-muted-foreground">
                  5-50 · cuanto más bajo, más sensible.
                </span>
              </div>
            </div>

            <div>
              <Label className="text-xs font-medium">Ventana de comparación (días)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  min={7} max={90}
                  value={cfg.window_days}
                  onChange={(e) => setCfg({ ...cfg, window_days: parseInt(e.target.value, 10) || 7 })}
                  onBlur={(e) => {
                    const v = Math.max(7, Math.min(90, parseInt(e.target.value, 10) || 30));
                    persist({ window_days: v });
                  }}
                  className="h-8 w-24"
                  disabled={saving}
                  data-testid="inventory-drop-window-input"
                />
                <span className="text-[11px] text-muted-foreground">
                  7-90 · compara el último cierre con uno de hace N días.
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border bg-muted/50 p-3">
              <div>
                <div className="font-medium">Aviso por email</div>
                <p className="text-[11px] text-muted-foreground">
                  Se envía al email del contable configurado.
                </p>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cfg.email_enabled}
                  onChange={(e) => persist({ email_enabled: e.target.checked })}
                  disabled={saving}
                  data-testid="inventory-drop-email-switch"
                  className="h-4 w-4 accent-rose-600"
                />
                <span className="text-[12px]">{cfg.email_enabled ? "Sí" : "No"}</span>
              </label>
            </div>

            {preview && (
              <div className={`rounded-lg border p-3 text-[12px] ${
                preview.triggered
                  ? "bg-rose-50 border-rose-200 text-rose-900"
                  : "bg-emerald-50 border-emerald-200 text-emerald-900"
              }`} data-testid="inventory-drop-preview">
                <div className="font-semibold mb-0.5">
                  {preview.triggered ? "Alerta disparada" : "Todo dentro del rango normal"}
                </div>
                <div>
                  Caída: <b>{preview.drop_pct}%</b> · {fmtEUR(preview.drop_value)}{" "}
                  (de {fmtEUR(preview.previous_value)} a {fmtEUR(preview.current_value)})
                </div>
                <div>
                  Compras en ventana: {fmtEUR(preview.purchases_in_window)} ·
                  Sin justificar: <b>{fmtEUR(preview.unexplained_drop)}</b>
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter className="flex justify-between sm:justify-between gap-2 flex-wrap">
          <Button
            variant="outline" size="sm"
            onClick={runNow}
            disabled={running || loading}
            data-testid="inventory-drop-run-now-btn"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <AlertTriangle className="h-4 w-4 mr-1" />}
            Evaluar ahora
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={saving || running}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ── Production closing section inside DayDetail ─────────────────────────
const EMPTY_ITEM = { name: "", qty: "", unit: "ud", shelf_life_days: "", prepared_by: "", notes: "", link_type: null, linked_id: null };
const EMPTY_WASTE = { name: "", qty: "", unit: "ud", reason: "expired", value_lost: "" };
const WASTE_REASONS = [
  { value: "expired", label: "Caducado" },
  { value: "quality", label: "Calidad" },
  { value: "accident", label: "Accidente" },
  { value: "other", label: "Otro" },
];

function ProductionClosingSection({
  restaurantId, day, productionStatus, productionLogId,
  productionItemsCount, productionWasteCount, productionWasteValue,
  onRefresh,
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Editable form state
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [wasteItems, setWasteItems] = useState([]);
  const [notes, setNotes] = useState("");

  // Recipe catalogue (loaded lazily when dialog opens)
  const [dishesCatalog, setDishesCatalog] = useState([]);
  const [batchRecipesCatalog, setBatchRecipesCatalog] = useState([]);

  // AI suggestion state for pending items (detail view)
  const [suggestingItemId, setSuggestingItemId] = useState(null);
  const [suggestionResult, setSuggestionResult] = useState(null);

  const hasLog = productionStatus === "closed" && productionLogId;

  const loadCatalogs = useCallback(async () => {
    try {
      const [menus, batches] = await Promise.all([
        api.getMenus(restaurantId).catch(() => []),
        api.listBatchRecipes(restaurantId).catch(() => ({ batch_recipes: [] })),
      ]);
      // Flatten dishes from menus (only those with recipes — we can't tell upfront,
      // so we keep all dishes and the backend will mark the item as pending if
      // recipe is missing)
      const dishes = [];
      (menus || []).forEach(menu => {
        (menu?.categories || []).forEach(cat => {
          (cat?.dishes || []).forEach(d => {
            if (d?.dish_id && d?.name) {
              dishes.push({ dish_id: d.dish_id, name: d.name, category: cat?.name });
            }
          });
        });
      });
      setDishesCatalog(dishes);
      setBatchRecipesCatalog(batches?.batch_recipes || []);
    } catch (e) {
      console.warn("loadCatalogs", e);
    }
  }, [restaurantId]);

  const loadDetail = useCallback(async () => {
    if (!hasLog) return;
    setLoading(true);
    try {
      const d = await api.getProductionLogByDay(restaurantId, day);
      setDetail(d);
    } catch (e) {
      toast.error(e?.message || "No se pudo cargar el log de producción");
    } finally {
      setLoading(false);
    }
  }, [restaurantId, day, hasLog]);

  useEffect(() => {
    if (expanded && hasLog && !detail) loadDetail();
  }, [expanded, hasLog, detail, loadDetail]);

  const openCreate = useCallback(async () => {
    // Load catalogs in parallel with detail
    loadCatalogs();
    // If editing, pre-fill from existing log
    if (hasLog) {
      try {
        const d = detail || await api.getProductionLogByDay(restaurantId, day);
        setDetail(d);
        setItems((d.items || []).map(it => ({
          name: it.name || "",
          qty: it.qty != null ? String(it.qty) : "",
          unit: it.unit || "ud",
          shelf_life_days: it.shelf_life_days != null ? String(it.shelf_life_days) : "",
          prepared_by: it.prepared_by || "",
          notes: it.notes || "",
          link_type: it.link_type || null,
          linked_id: it.linked_id || null,
        })));
        setWasteItems((d.waste_items || []).map(w => ({
          name: w.name || "",
          qty: w.qty != null ? String(w.qty) : "",
          unit: w.unit || "ud",
          reason: w.reason || "expired",
          value_lost: w.value_lost != null ? String(w.value_lost) : "",
        })));
        setNotes(d.notes || "");
      } catch (e) {
        toast.error(e?.message || "Error al cargar log existente");
      }
    } else {
      setItems([{ ...EMPTY_ITEM }]);
      setWasteItems([]);
      setNotes("");
    }
    setShowDialog(true);
  }, [hasLog, detail, restaurantId, day, loadCatalogs]);

  const handleSave = useCallback(async () => {
    const cleanItems = items
      .filter(i => (i.name || "").trim() && Number(i.qty) > 0)
      .map(i => ({
        name: i.name.trim(),
        qty: Number(i.qty),
        unit: (i.unit || "ud").trim() || "ud",
        shelf_life_days: i.shelf_life_days ? Number(i.shelf_life_days) : null,
        prepared_by: (i.prepared_by || "").trim() || null,
        notes: (i.notes || "").trim() || null,
        link_type: i.link_type || null,
        linked_id: i.linked_id || null,
      }));
    const cleanWaste = wasteItems
      .filter(w => (w.name || "").trim() && Number(w.qty) > 0)
      .map(w => ({
        name: w.name.trim(),
        qty: Number(w.qty),
        unit: (w.unit || "ud").trim() || "ud",
        reason: w.reason || "expired",
        value_lost: w.value_lost ? Number(w.value_lost) : 0,
      }));
    if (!cleanItems.length && !cleanWaste.length) {
      toast.error("Añade al menos una preparación o una merma antes de guardar");
      return;
    }
    setBusy(true);
    try {
      const res = await api.upsertProductionLog(restaurantId, {
        day, items: cleanItems, wasteItems: cleanWaste, notes,
      });
      const linked = res.linked_count || 0;
      const pending = res.pending_count || 0;
      toast.success(
        `Producción cerrada · ${res.items_count} preparaciones` +
        (linked ? ` · ${linked} vinculadas al stock` : "") +
        (pending ? ` · ${pending} pendientes de vincular` : "") +
        (res.waste_count ? ` · ${res.waste_count} mermas (${fmtEUR(res.waste_value)})` : ""),
      );
      setShowDialog(false);
      setDetail(null);
      setExpanded(true);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e?.message || "Error guardando la producción");
    } finally {
      setBusy(false);
    }
  }, [restaurantId, day, items, wasteItems, notes, onRefresh]);

  const handleDelete = useCallback(async () => {
    if (!hasLog) return;
    if (!confirm("¿Eliminar el log de producción de este día?")) return;
    setBusy(true);
    try {
      await api.deleteProductionLog(restaurantId, productionLogId);
      toast.success("Log de producción eliminado");
      setDetail(null);
      setExpanded(false);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e?.message || "No se pudo eliminar");
    } finally {
      setBusy(false);
    }
  }, [restaurantId, productionLogId, hasLog, onRefresh]);

  const updateItem = (idx, patch) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const addItem = () => setItems(prev => [...prev, { ...EMPTY_ITEM }]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
  const updateWaste = (idx, patch) => setWasteItems(prev => prev.map((w, i) => i === idx ? { ...w, ...patch } : w));
  const addWaste = () => setWasteItems(prev => [...prev, { ...EMPTY_WASTE }]);
  const removeWaste = (idx) => setWasteItems(prev => prev.filter((_, i) => i !== idx));

  // ── Detail view actions: AI-suggest + post-hoc linking ──
  const handleAISuggest = useCallback(async (item) => {
    if (!item?.name) return;
    setSuggestingItemId(item.item_id);
    setSuggestionResult(null);
    try {
      const res = await api.suggestBatchRecipe(restaurantId, {
        name: item.name,
        yieldQty: item.qty,
        yieldUnit: item.unit || "ud",
      });
      setSuggestionResult({ ...res, sourceItem: item });
    } catch (e) {
      toast.error(e?.message || "No se pudo generar la sugerencia IA");
      setSuggestingItemId(null);
    }
  }, [restaurantId]);

  const handleConfirmSuggestion = useCallback(async () => {
    if (!suggestionResult || !suggestionResult.sourceItem) return;
    const item = suggestionResult.sourceItem;
    setBusy(true);
    try {
      // Create batch recipe from the suggestion (filter out ingredients without ingredient_id)
      const validIngs = (suggestionResult.ingredients || [])
        .filter(i => i.ingredient_id)
        .map(i => ({
          ingredient_id: i.ingredient_id,
          quantity: Number(i.quantity) || 0,
          unit: i.unit || null,
        }));
      if (!validIngs.length) {
        toast.error("La IA no pudo vincular ningún ingrediente del inventario. Crea la ficha manualmente.");
        setBusy(false);
        return;
      }
      const batch = await api.createBatchRecipe(restaurantId, {
        name: item.name,
        yield_qty: Number(item.qty) || 1,
        yield_unit: item.unit || "ud",
        ingredients: validIngs,
        preparation_notes: suggestionResult.notes || null,
        shelf_life_days: suggestionResult.shelf_life_days || null,
      });
      // Link the production item to the newly-created batch recipe
      const linkRes = await api.linkProductionItem(restaurantId, productionLogId, item.item_id, {
        linkType: "batch_recipe",
        linkedId: batch.batch_recipe_id,
      });
      const created = linkRes?.movements_created || 0;
      toast.success(`Ficha "${item.name}" creada y vinculada · ${created} movimientos de stock descontados`);
      setSuggestionResult(null);
      setSuggestingItemId(null);
      setDetail(null);  // force reload
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e?.message || "Error al aplicar la sugerencia");
    } finally {
      setBusy(false);
    }
  }, [suggestionResult, restaurantId, productionLogId, onRefresh]);

  const handleIgnoreItem = useCallback(async (itemId) => {
    if (!confirm("¿Marcar como 'no consume stock'? No se generarán movimientos de inventario para esta preparación.")) return;
    setBusy(true);
    try {
      await api.linkProductionItem(restaurantId, productionLogId, itemId, {
        linkType: "ignored",
        linkedId: null,
      });
      toast.success("Marcada como sin consumo de stock");
      setDetail(null);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e?.message || "Error al actualizar");
    } finally {
      setBusy(false);
    }
  }, [restaurantId, productionLogId, onRefresh]);

  return (
    <div className="mt-4 border-t pt-3" data-testid="production-closing-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h4 className="font-medium text-sm flex items-center gap-1.5">
          <ChefHat className="h-3.5 w-3.5 text-orange-700" /> Cierre de producción
        </h4>
        {hasLog ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[11px] text-orange-700 font-medium" data-testid="production-closing-summary">
              <CheckCircle2 className="h-3 w-3" />
              <b>{productionItemsCount}</b> preparaciones
              {(productionWasteCount || 0) > 0 && (
                <>
                  {" · "}
                  <span className="text-rose-700">
                    <b>{productionWasteCount}</b> mermas · {fmtEUR(productionWasteValue || 0)}
                  </span>
                </>
              )}
            </span>
            <Button
              size="sm" variant="outline"
              onClick={() => setExpanded(v => !v)}
              data-testid="production-closing-toggle"
            >
              {expanded ? "Ocultar" : "Ver detalles"}
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={openCreate}
              disabled={busy}
              data-testid="production-closing-edit"
            >
              Editar
            </Button>
            <Button
              size="sm" variant="ghost"
              onClick={handleDelete}
              disabled={busy}
              className="text-rose-700 hover:text-rose-700 hover:bg-rose-50"
              data-testid="production-closing-delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <Button size="sm" onClick={openCreate} data-testid="production-closing-create">
            <ChefHat className="h-3.5 w-3.5 mr-1" /> Cerrar producción
          </Button>
        )}
      </div>

      {hasLog && expanded && (
        <div className="mt-3 rounded-lg border bg-muted/50 p-3 text-sm" data-testid="production-closing-detail">
          {loading ? (
            <div className="flex justify-center py-3"><Loader2 className="h-4 w-4 animate-spin" /></div>
          ) : detail ? (
            <>
              {(detail.items || []).length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      Preparaciones ({detail.items.length})
                    </div>
                    <div className="flex items-center gap-2 text-[10px]">
                      {(detail.linked_count || 0) > 0 && (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                          <CheckCircle2 className="h-2.5 w-2.5" /> {detail.linked_count} descuenta stock
                        </span>
                      )}
                      {(detail.pending_count || 0) > 0 && (
                        <span className="inline-flex items-center gap-1 text-amber-700 font-medium">
                          <AlertTriangle className="h-2.5 w-2.5" /> {detail.pending_count} pendiente{detail.pending_count > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="max-h-72 overflow-auto rounded border bg-card">
                    <table className="w-full text-xs">
                      <thead className="bg-muted text-muted-foreground">
                        <tr>
                          <th className="text-left py-1 px-2">Producto</th>
                          <th className="text-right px-2">Cantidad</th>
                          <th className="text-center px-2">Stock</th>
                          <th className="text-center px-2">Caduca</th>
                          <th className="text-right px-2">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.items.map(it => {
                          const ls = it.link_status || "pending";
                          return (
                            <tr key={it.item_id} className="border-t border-border" data-testid={`production-detail-row-${it.item_id}`}>
                              <td className="py-1 px-2 truncate max-w-[200px]" title={it.name}>
                                {it.name}
                                {it.prepared_by && (
                                  <span className="block text-[10px] text-muted-foreground">{it.prepared_by}</span>
                                )}
                              </td>
                              <td className="text-right px-2 tabular-nums">{it.qty} {it.unit}</td>
                              <td className="text-center px-2">
                                {ls === "linked" ? (
                                  <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded font-medium" title={`${(it.stock_movement_ids || []).length} movimientos generados`}>
                                    <CheckCircle2 className="h-2.5 w-2.5" /> Descontado
                                  </span>
                                ) : ls === "ignored" ? (
                                  <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                    ○ Ignorado
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded font-medium">
                                    <AlertTriangle className="h-2.5 w-2.5" /> Pendiente
                                  </span>
                                )}
                              </td>
                              <td className="text-center px-2 text-[10px]">
                                {it.expires_at ? (
                                  <span className="inline-flex items-center gap-1 text-amber-700">
                                    <Clock className="h-3 w-3" /> {it.expires_at}
                                  </span>
                                ) : "—"}
                              </td>
                              <td className="text-right px-2">
                                {ls === "pending" && (
                                  <div className="flex items-center gap-1 justify-end">
                                    <Button
                                      size="sm" variant="outline"
                                      className="h-6 px-2 text-[10px] border-violet-300 text-violet-700 hover:bg-violet-50"
                                      onClick={() => handleAISuggest(it)}
                                      disabled={busy || suggestingItemId === it.item_id}
                                      data-testid={`production-item-ai-suggest-${it.item_id}`}
                                    >
                                      {suggestingItemId === it.item_id && !suggestionResult ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                      ) : (
                                        <><Sparkles className="h-3 w-3 mr-0.5" /> IA</>
                                      )}
                                    </Button>
                                    <Button
                                      size="sm" variant="ghost"
                                      className="h-6 px-2 text-[10px] text-muted-foreground"
                                      onClick={() => handleIgnoreItem(it.item_id)}
                                      disabled={busy}
                                      data-testid={`production-item-ignore-${it.item_id}`}
                                      title="Marcar como 'no consume stock'"
                                    >
                                      Ignorar
                                    </Button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {(detail.waste_items || []).length > 0 && (
                <div className="mt-3">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                    Mermas ({detail.waste_items.length} · {fmtEUR(detail.waste_value || 0)})
                  </div>
                  <div className="max-h-40 overflow-auto rounded border bg-card">
                    <table className="w-full text-xs">
                      <thead className="bg-rose-50 text-rose-700">
                        <tr>
                          <th className="text-left py-1 px-2">Producto</th>
                          <th className="text-right px-2">Cantidad</th>
                          <th className="text-left px-2">Motivo</th>
                          <th className="text-right pr-2">Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.waste_items.map(w => (
                          <tr key={w.item_id} className="border-t border-border">
                            <td className="py-1 px-2 truncate max-w-[220px]">{w.name}</td>
                            <td className="text-right px-2 tabular-nums">{w.qty} {w.unit}</td>
                            <td className="px-2 text-[11px] text-muted-foreground">
                              {WASTE_REASONS.find(r => r.value === w.reason)?.label || w.reason}
                            </td>
                            <td className="text-right pr-2 tabular-nums text-rose-700">{fmtEUR(w.value_lost || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {detail.notes && (
                <p className="mt-2 text-[11px] text-muted-foreground italic">Nota: {detail.notes}</p>
              )}
            </>
          ) : (
            <p className="text-muted-foreground">No se pudo cargar el detalle.</p>
          )}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={(v) => !busy && setShowDialog(v)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="production-closing-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ChefHat className="h-5 w-5 text-orange-700" />
              {hasLog ? "Editar producción" : "Cerrar producción del día"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2 text-sm">
            <p className="text-muted-foreground text-[13px]">
              Registra lo preparado (mise en place) y las mermas del día <b>{day}</b>. Las caducidades se calculan automáticamente según los días de vida útil.
            </p>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="font-medium">Preparaciones</Label>
                <Button size="sm" variant="outline" onClick={addItem} type="button" data-testid="production-add-item">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Añadir
                </Button>
              </div>
              <div className="space-y-2">
                {items.map((it, idx) => {
                  const linkedLabel = it.link_type === "dish_recipe"
                    ? (dishesCatalog.find(d => d.dish_id === it.linked_id)?.name || "Plato")
                    : it.link_type === "batch_recipe"
                    ? (batchRecipesCatalog.find(b => b.batch_recipe_id === it.linked_id)?.name || "Ficha")
                    : it.link_type === "ignored"
                    ? "Sin consumo"
                    : null;
                  const selectValue = it.link_type && it.linked_id
                    ? `${it.link_type}:${it.linked_id}`
                    : it.link_type === "ignored"
                    ? "ignored:"
                    : "";
                  const onLinkChange = (v) => {
                    if (!v) {
                      updateItem(idx, { link_type: null, linked_id: null });
                      return;
                    }
                    const [type, id] = v.split(":");
                    if (type === "ignored") {
                      updateItem(idx, { link_type: "ignored", linked_id: null });
                    } else {
                      updateItem(idx, { link_type: type, linked_id: id });
                    }
                  };
                  return (
                    <div key={idx} className="rounded border bg-card p-2 space-y-1.5" data-testid={`production-item-row-${idx}`}>
                      <div className="grid grid-cols-12 gap-1.5">
                        <Input className="col-span-4 h-8" placeholder="Nombre (ej. Salsa boloñesa)" value={it.name} onChange={(e) => updateItem(idx, { name: e.target.value })} />
                        <Input className="col-span-2 h-8" type="number" placeholder="Cant." value={it.qty} onChange={(e) => updateItem(idx, { qty: e.target.value })} />
                        <Input className="col-span-1 h-8" placeholder="L" value={it.unit} onChange={(e) => updateItem(idx, { unit: e.target.value })} />
                        <Input className="col-span-2 h-8" type="number" placeholder="Días vida" value={it.shelf_life_days} onChange={(e) => updateItem(idx, { shelf_life_days: e.target.value })} title="Días de vida útil desde hoy" />
                        <Input className="col-span-2 h-8" placeholder="Chef (opcional)" value={it.prepared_by} onChange={(e) => updateItem(idx, { prepared_by: e.target.value })} />
                        <Button size="sm" variant="ghost" className="col-span-1 text-rose-700 hover:bg-rose-50 h-8 px-2" onClick={() => removeItem(idx)} type="button" data-testid={`production-remove-item-${idx}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-[11px] text-muted-foreground shrink-0">Vincular a:</Label>
                        <select
                          className="flex-1 h-7 rounded border px-2 text-xs bg-card"
                          value={selectValue}
                          onChange={(e) => onLinkChange(e.target.value)}
                          data-testid={`production-item-link-${idx}`}
                        >
                          <option value="">— Sin vincular (pendiente) —</option>
                          {dishesCatalog.length > 0 && (
                            <optgroup label="Platos de carta">
                              {dishesCatalog.map(d => (
                                <option key={`d-${d.dish_id}`} value={`dish_recipe:${d.dish_id}`}>
                                  {d.name}{d.category ? ` · ${d.category}` : ""}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          {batchRecipesCatalog.length > 0 && (
                            <optgroup label="Fichas de producción">
                              {batchRecipesCatalog.map(b => (
                                <option key={`b-${b.batch_recipe_id}`} value={`batch_recipe:${b.batch_recipe_id}`}>
                                  {b.name} ({b.yield_qty} {b.yield_unit})
                                </option>
                              ))}
                            </optgroup>
                          )}
                          <option value="ignored:">No consume stock (ignorar)</option>
                        </select>
                        {linkedLabel && (
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                            it.link_type === "ignored"
                              ? "bg-muted text-muted-foreground"
                              : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          }`}>
                            {it.link_type === "ignored" ? "○" : "✓"} {linkedLabel}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="font-medium flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5 text-rose-700" />
                  Mermas
                </Label>
                <Button size="sm" variant="outline" onClick={addWaste} type="button" data-testid="production-add-waste">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Añadir
                </Button>
              </div>
              {wasteItems.length === 0 ? (
                <p className="text-[11px] text-muted-foreground italic">Sin mermas registradas.</p>
              ) : (
                <div className="space-y-2">
                  {wasteItems.map((w, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-1.5 p-2 rounded border bg-rose-50/40" data-testid={`production-waste-row-${idx}`}>
                      <Input className="col-span-4 h-8" placeholder="Nombre" value={w.name} onChange={(e) => updateWaste(idx, { name: e.target.value })} />
                      <Input className="col-span-2 h-8" type="number" placeholder="Cant." value={w.qty} onChange={(e) => updateWaste(idx, { qty: e.target.value })} />
                      <Input className="col-span-1 h-8" placeholder="ud" value={w.unit} onChange={(e) => updateWaste(idx, { unit: e.target.value })} />
                      <select
                        className="col-span-2 h-8 rounded border px-2 text-xs bg-card"
                        value={w.reason}
                        onChange={(e) => updateWaste(idx, { reason: e.target.value })}
                      >
                        {WASTE_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                      <Input className="col-span-2 h-8" type="number" placeholder="€ perdidos" value={w.value_lost} onChange={(e) => updateWaste(idx, { value_lost: e.target.value })} />
                      <Button size="sm" variant="ghost" className="col-span-1 text-rose-700 hover:bg-rose-100 h-8 px-2" onClick={() => removeWaste(idx)} type="button" data-testid={`production-remove-waste-${idx}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="production-notes">Nota (opcional)</Label>
              <Input
                id="production-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Observaciones del turno"
                data-testid="production-closing-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={busy} data-testid="production-closing-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ChefHat className="h-4 w-4 mr-1" />}
              {hasLog ? "Guardar cambios" : "Guardar cierre"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Suggestion dialog for pending items (detail view) */}
      <Dialog
        open={!!suggestionResult}
        onOpenChange={(v) => {
          if (!v && !busy) {
            setSuggestionResult(null);
            setSuggestingItemId(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="production-ai-suggest-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-violet-600" />
              Sugerencia de ficha de producción
            </DialogTitle>
          </DialogHeader>
          {suggestionResult && (
            <div className="space-y-4 pt-2 text-sm">
              <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-3">
                <div className="font-medium text-violet-900">{suggestionResult.sourceItem?.name}</div>
                <div className="text-[11px] text-violet-700">
                  Rendimiento: {suggestionResult.sourceItem?.qty} {suggestionResult.sourceItem?.unit}
                  {suggestionResult.shelf_life_days ? ` · vida útil ${suggestionResult.shelf_life_days}d` : ""}
                </div>
                {suggestionResult.notes && (
                  <p className="text-[11px] text-violet-800 mt-1 italic">{suggestionResult.notes}</p>
                )}
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                  Ingredientes propuestos ({(suggestionResult.ingredients || []).length})
                </div>
                <div className="max-h-64 overflow-auto rounded border bg-card">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 text-muted-foreground">
                      <tr>
                        <th className="text-left py-1 px-2">Ingrediente</th>
                        <th className="text-right px-2">Cantidad</th>
                        <th className="text-center px-2">Unidad</th>
                        <th className="text-center px-2">Inventario</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(suggestionResult.ingredients || []).map((ing, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="py-1 px-2">{ing.name_guess || "(sin nombre)"}</td>
                          <td className="text-right px-2 tabular-nums">{ing.quantity}</td>
                          <td className="text-center px-2 text-muted-foreground">{ing.unit || "—"}</td>
                          <td className="text-center px-2">
                            {ing.ingredient_id ? (
                              <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded font-medium">
                                <CheckCircle2 className="h-2.5 w-2.5" /> Vinculado
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                Sin match
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {(suggestionResult.ingredients || []).some(i => !i.ingredient_id) && (
                  <p className="text-[10px] text-amber-700 mt-1">
                    Sólo los ingredientes con match serán descontados del stock. Puedes editar la ficha después en la pestaña Recetas para completarla.
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setSuggestionResult(null);
                setSuggestingItemId(null);
              }}
              disabled={busy}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmSuggestion}
              disabled={busy}
              data-testid="production-ai-suggest-confirm"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
              Crear ficha y vincular
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}



// ── AI Root-Cause Analysis modal for inventory drop alert ──────────────
const PROB_COLORS = {
  alta: "bg-rose-100 text-rose-900 border-rose-300",
  media: "bg-amber-100 text-amber-900 border-amber-300",
  baja: "bg-muted text-foreground border-border",
};
const PRIORITY_COLORS = {
  1: "bg-rose-100 text-rose-900 border-rose-300",
  2: "bg-amber-100 text-amber-900 border-amber-300",
  3: "bg-sky-100 text-sky-900 border-sky-300",
};

function InventoryDropRcaDialog({ restaurantId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [verdict, setVerdict] = useState(null);
  const [cached, setCached] = useState(false);
  const [error, setError] = useState(null);

  const fetchAnalysis = useCallback(async (force = false) => {
    setError(null);
    if (force) setRegenerating(true); else setLoading(true);
    try {
      const res = await api.analyzeInventoryDropAlert(restaurantId, { force });
      setAnalysis(res.analysis);
      setVerdict(res.verdict);
      setCached(!!res.cached);
    } catch (e) {
      setError(e?.message || "No se pudo generar el análisis");
    } finally {
      setLoading(false);
      setRegenerating(false);
    }
  }, [restaurantId]);

  useEffect(() => { fetchAnalysis(false); }, [fetchAnalysis]);

  const onFeedback = useCallback(async (target, index, status) => {
    try {
      const res = await api.setDropAlertFeedback(restaurantId, { target, index, status });
      setAnalysis(res.analysis);
      const verb = status ? (
        target === "cause"
          ? (status === "verified" ? "verificada" : "descartada")
          : "completada"
      ) : "sin marcar";
      toast.success(`Marcada como ${verb}`);
    } catch (e) {
      toast.error(e?.message || "No se pudo guardar el feedback");
    }
  }, [restaurantId]);

  return (
    <Dialog open={true} onOpenChange={(v) => !loading && !regenerating && !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="inventory-drop-rca-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-violet-600" />
            Análisis de causas con IA
            {cached && !regenerating && (
              <span className="ml-2 text-[10px] font-normal px-1.5 py-0.5 rounded bg-muted text-foreground border">
                caché
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center space-y-2">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-violet-600" />
            <p className="text-sm text-muted-foreground">
              Analizando 90 días de movimientos, mermas y cambios de coste…
            </p>
            <p className="text-[11px] text-muted-foreground">
              Esto puede tardar 15-30 segundos.
            </p>
          </div>
        )}

        {error && (
          <div className="p-4 rounded-lg border border-rose-200 bg-rose-50 text-sm text-rose-900" data-testid="inventory-drop-rca-error">
            {error}
          </div>
        )}

        {!loading && analysis && verdict && (
          <div className="space-y-4 pt-1 text-sm">
            {/* Verdict snapshot */}
            <div className="rounded-lg border bg-muted/50 p-3 text-[12px]" data-testid="inventory-drop-rca-verdict">
              <div className="font-medium mb-1">Alerta analizada</div>
              <div className="text-muted-foreground">
                Del <b>{verdict.previous_day}</b> ({fmtEUR(verdict.previous_value)})
                al <b>{verdict.current_day}</b> ({fmtEUR(verdict.current_value)}) ·
                caída {verdict.drop_pct}% ·
                sin justificar: <b className="text-rose-700">{fmtEUR(verdict.unexplained_drop)}</b>
              </div>
            </div>

            {analysis.alerta_critica && (
              <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-[13px] text-rose-900 flex items-start gap-2" data-testid="inventory-drop-rca-critical">
                <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
                <span><b>Conclusión crítica:</b> {analysis.alerta_critica}</span>
              </div>
            )}

            <div>
              <div className="font-semibold mb-2 flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4 text-amber-700" /> Causas probables
              </div>
              <div className="space-y-2">
                {(analysis.causas_probables || []).map((c, idx) => (
                  <div
                    key={idx}
                    className={`rounded-lg border p-3 transition ${
                      c._feedback_status === "verified" ? "border-emerald-300 bg-emerald-50/50"
                      : c._feedback_status === "discarded" ? "border-border bg-muted opacity-70"
                      : "bg-card"
                    }`}
                    data-testid={`rca-causa-${idx}`}
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className={`font-medium ${c._feedback_status === "discarded" ? "line-through text-muted-foreground" : ""}`}>
                        {c.titulo}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase ${PROB_COLORS[c.probabilidad] || PROB_COLORS.media}`}>
                          {c.probabilidad}
                        </span>
                        {c.impacto_estimado_eur > 0 && (
                          <span className="text-[11px] text-rose-700 font-semibold">
                            ~{fmtEUR(c.impacto_estimado_eur)}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-[12px] text-muted-foreground mt-1">{c.descripcion}</p>
                    <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                      <Button
                        size="sm" variant={c._feedback_status === "verified" ? "default" : "outline"}
                        className={c._feedback_status === "verified" ? "bg-emerald-600 hover:bg-emerald-700 h-7 px-2" : "h-7 px-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50"}
                        onClick={() => onFeedback("cause", idx, c._feedback_status === "verified" ? null : "verified")}
                        data-testid={`rca-causa-verify-${idx}`}
                      >
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        {c._feedback_status === "verified" ? "Verificada" : "Verificar"}
                      </Button>
                      <Button
                        size="sm" variant={c._feedback_status === "discarded" ? "default" : "outline"}
                        className={c._feedback_status === "discarded" ? "bg-gray-500 hover:bg-gray-600 h-7 px-2" : "h-7 px-2 text-foreground hover:bg-gray-50"}
                        onClick={() => onFeedback("cause", idx, c._feedback_status === "discarded" ? null : "discarded")}
                        data-testid={`rca-causa-discard-${idx}`}
                      >
                        <X className="h-3 w-3 mr-1" />
                        {c._feedback_status === "discarded" ? "Descartada" : "Descartar"}
                      </Button>
                      {c._feedback_note && (
                        <span className="text-[11px] text-emerald-700 italic truncate max-w-[240px]" title={c._feedback_note}>
                          · {c._feedback_note}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="font-semibold mb-2 flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-700" /> Plan de acción priorizado
              </div>
              <div className="space-y-2">
                {(analysis.plan_accion || [])
                  .slice()
                  .sort((a, b) => (a.prioridad || 99) - (b.prioridad || 99))
                  .map((a, sortedIdx) => {
                    // Find original index so feedback can target the right slot
                    const origIdx = (analysis.plan_accion || []).indexOf(a);
                    return (
                      <div
                        key={origIdx}
                        className={`rounded-lg border p-3 flex items-start gap-2 transition ${
                          a._feedback_status === "completed" ? "border-emerald-300 bg-emerald-50/50" : "bg-card"
                        }`}
                        data-testid={`rca-accion-${sortedIdx}`}
                      >
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-bold shrink-0 ${PRIORITY_COLORS[a.prioridad] || PRIORITY_COLORS[2]}`}>
                          P{a.prioridad}
                        </span>
                        <div className="flex-1">
                          <div className={`font-medium ${a._feedback_status === "completed" ? "line-through text-muted-foreground" : ""}`}>
                            {a.accion}
                          </div>
                          <p className="text-[12px] text-muted-foreground mt-0.5">{a.como}</p>
                          <div className="mt-1.5">
                            <Button
                              size="sm" variant={a._feedback_status === "completed" ? "default" : "outline"}
                              className={a._feedback_status === "completed" ? "bg-emerald-600 hover:bg-emerald-700 h-7 px-2" : "h-7 px-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50"}
                              onClick={() => onFeedback("action", origIdx, a._feedback_status === "completed" ? null : "completed")}
                              data-testid={`rca-accion-complete-${origIdx}`}
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              {a._feedback_status === "completed" ? "Completada" : "Marcar como completada"}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div className="text-[11px] text-muted-foreground italic pt-2 border-t">
              Análisis generado por IA · Claude Sonnet 4.5 · Puede contener imprecisiones.
              Tu feedback (verificar/descartar/completar) entrena al asistente para próximas alertas.
              Valida siempre la información con conteos físicos antes de tomar decisiones críticas.
            </div>
          </div>
        )}

        <DialogFooter className="flex justify-between gap-2 sm:justify-between flex-wrap">
          <Button
            variant="outline" size="sm"
            onClick={() => fetchAnalysis(true)}
            disabled={loading || regenerating}
            data-testid="inventory-drop-rca-regenerate"
          >
            {regenerating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Regenerar análisis
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={loading || regenerating}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ── Monthly pre-closing checklist ──────────────────────────────────────
const CHECKLIST_TONE = {
  ok: {
    row: "bg-emerald-50/40 border-emerald-200",
    icon: "text-emerald-700",
    badge: "bg-emerald-100 text-emerald-800 border-emerald-300",
  },
  warning: {
    row: "bg-amber-50/60 border-amber-200",
    icon: "text-amber-700",
    badge: "bg-amber-100 text-amber-900 border-amber-300",
  },
  error: {
    row: "bg-rose-50/60 border-rose-200",
    icon: "text-rose-700",
    badge: "bg-rose-100 text-rose-900 border-rose-300",
  },
};

function MonthlyPreCloseChecklist({ restaurantId, year, month }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const res = await api.getMonthlyPreCloseChecklist(restaurantId, { year, month });
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, year, month]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Card data-testid="monthly-preclose-checklist">
        <CardContent className="p-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando checklist de pre-cierre…
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const s = data.summary || {};
  const headerTone = data.ready_to_close
    ? { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", label: "Listo para cerrar el mes" }
    : s.errors > 0
      ? { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-900", label: "Pendientes críticos" }
      : { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-900", label: "Casi listo · quedan avisos" };

  return (
    <Card data-testid="monthly-preclose-checklist">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-semibold flex items-center gap-2">
            <CalendarCheck className="h-4 w-4 text-indigo-600" />
            Checklist de pre-cierre mensual
            <span className="text-[11px] text-muted-foreground font-normal">
              {MONTH_NAMES_ES[data.month - 1]} {data.year}
            </span>
          </h3>
          <div className={`px-2.5 py-1 rounded-full border text-[12px] font-medium ${headerTone.bg} ${headerTone.border} ${headerTone.text}`}>
            {data.ready_to_close ? <CheckCircle2 className="inline h-3.5 w-3.5 mr-1" /> : <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />}
            {headerTone.label}
            <span className="ml-2 opacity-70">
              {s.ok}/{s.total} OK · {s.warnings} avisos · {s.errors} errores
            </span>
          </div>
        </div>

        <div className="space-y-1.5">
          {(data.items || []).map((it) => {
            const tone = CHECKLIST_TONE[it.status] || CHECKLIST_TONE.warning;
            const Icon = it.status === "ok" ? CheckCircle2 : it.status === "error" ? AlertTriangle : CircleDashed;
            return (
              <div
                key={it.id}
                className={`rounded-lg border px-3 py-2 flex items-start gap-2 ${tone.row}`}
                data-testid={`checklist-item-${it.id}`}
              >
                <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${tone.icon}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-medium text-sm">{it.label}</div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold uppercase ${tone.badge}`}>
                      {it.status === "ok" ? "OK" : it.status === "warning" ? "Aviso" : "Pendiente"}
                    </span>
                  </div>
                  <p className="text-[12px] text-muted-foreground mt-0.5">{it.detail}</p>
                  {(it.open_sessions || []).length > 0 && (
                    <p className="text-[11px] text-rose-700 mt-1">
                      Cajas abiertas: {(it.open_sessions || []).join(", ")}
                    </p>
                  )}
                  {(it.missing_items || []).length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {it.missing_items.map((m) => (
                        <span key={m.pattern_key} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border bg-card">
                          {m.label} · <span className="text-muted-foreground">{m.expected_date}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between pt-1 text-[11px] text-muted-foreground">
          <span>
            Este checklist se calcula al momento con tus datos reales.
            {data.month_is_past ? "" : " Aún quedan días por cerrar este mes."}
          </span>
          <Button size="sm" variant="ghost" onClick={load} data-testid="checklist-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Actualizar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}



// ── AI learnings: patterns the owner has verified/discarded over time ──
function AiLearningsCard({ restaurantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.getDropAlertLearnings(restaurantId, 8);
        if (!cancelled) setData(res);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [restaurantId]);

  if (loading || !data || !data.has_data) return null;

  const Section = ({ title, icon: Icon, tone, items, showImpact, showHow }) => {
    if (!items.length) return null;
    return (
      <div className="space-y-1.5">
        <div className={`text-[11px] uppercase tracking-wide font-semibold flex items-center gap-1.5 ${tone.text}`}>
          <Icon className="h-3.5 w-3.5" /> {title} · {items.length}
        </div>
        <div className="space-y-1.5">
          {items.map((p, idx) => (
            <div
              key={idx}
              className={`rounded-lg border ${tone.border} ${tone.bg} px-3 py-2`}
              data-testid={`ai-learning-${title.toLowerCase().replace(/\s+/g, "-")}-${idx}`}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className={`font-medium text-sm ${tone.text}`}>{p.title}</span>
                <div className="flex items-center gap-1.5">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${tone.badge}`}>
                    ×{p.count}
                  </span>
                  {showImpact && p.total_impact_eur > 0 && (
                    <span className="text-[11px] text-rose-700 font-semibold">
                      {fmtEUR(p.total_impact_eur)} acum.
                    </span>
                  )}
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">
                {showHow ? p.sample_how : p.sample_description}
              </p>
              <div className="flex items-center justify-between mt-1 flex-wrap gap-2">
                <span className="text-[10px] text-muted-foreground">
                  Última alerta: {p.last_seen}
                </span>
                {(p.notes || []).length > 0 && (
                  <span className="text-[10px] text-emerald-700 italic truncate max-w-[260px]" title={(p.notes || []).map(n => `${n.day}: ${n.note}`).join(" · ")}>
                    {p.notes.length === 1 ? p.notes[0].note : `${p.notes.length} notas · ${p.notes[0].note}`}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Card data-testid="ai-learnings-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-semibold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-600" />
            Memoria operativa del restaurante
          </h3>
          <span className="text-[11px] text-muted-foreground">
            Aprendido de {data.total_alerts_analysed} alert{data.total_alerts_analysed === 1 ? "a" : "as"} con tu feedback
          </span>
        </div>

        <p className="text-[12px] text-muted-foreground">
          Patrones recurrentes detectados por la IA y contrastados por ti.
          La próxima alerta usará esta memoria para diagnósticos más certeros.
        </p>

        <div className="grid md:grid-cols-3 gap-3">
          <Section
            title="Patrones verificados"
            icon={CheckCircle2}
            tone={{
              bg: "bg-emerald-50/50",
              border: "border-emerald-200",
              text: "text-emerald-900",
              badge: "bg-emerald-100 text-emerald-900 border border-emerald-300",
            }}
            items={data.verified_patterns || []}
            showImpact
          />
          <Section
            title="Descartados"
            icon={X}
            tone={{
              bg: "bg-muted/50",
              border: "border-border",
              text: "text-foreground",
              badge: "bg-accent text-foreground border border-border",
            }}
            items={data.discarded_patterns || []}
          />
          <Section
            title="Acciones efectivas"
            icon={ShieldAlert}
            tone={{
              bg: "bg-violet-50/50",
              border: "border-violet-200",
              text: "text-violet-900",
              badge: "bg-violet-100 text-violet-900 border border-violet-300",
            }}
            items={data.completed_actions || []}
            showHow
          />
        </div>
      </CardContent>
    </Card>
  );
}

